<template>
  <div class="mini-tab-content">
    <div
      v-if="loading"
      class="interc-loading"
    >
      <b-spinner
        small
        variant="secondary"
      />
      <span>{{ $t('involvedPanel.careLine.loading.spinner') }}</span>
    </div>

    <template v-else>
      <div
        v-if="disciplineId && careLines.length"
        class="cl-chips"
      >
        <button
          type="button"
          class="cl-chip"
          :class="{ 'cl-chip--on': selected === null }"
          :disabled="saving || readonly"
          @click="choose(null)"
        >
          {{ $t('involvedPanel.careLine.chips.none') }}
        </button>
        <button
          v-for="c in careLines"
          :key="c.id"
          type="button"
          class="cl-chip"
          :class="{ 'cl-chip--on': selected === c.id }"
          :disabled="saving || readonly"
          :title="c.description || c.name"
          @click="choose(c.id)"
        >
          {{ c.name }}<span v-if="Number(c.status) !== 1"> {{ $t('involvedPanel.careLine.chips.inactive') }}</span>
        </button>
      </div>

      <p
        v-if="selectedDescription"
        class="cl-desc"
      >
        {{ selectedDescription }}
      </p>

      <div
        v-if="selected !== null && setBy"
        class="interc-item__meta"
      >
        <feather-icon
          icon="UserIcon"
          size="11"
        />
        <span>{{ setBy }}</span>
        <template v-if="setAt">
          <span class="interc-item__dot" />
          <feather-icon
            icon="ClockIcon"
            size="11"
          />
          <span>{{ setAt | formatDate }}</span>
        </template>
      </div>

      <small
        v-if="!disciplineId"
        class="text-muted"
      >
        {{ $t('involvedPanel.careLine.empty.noDiscipline') }}
      </small>
      <small
        v-else-if="error"
        class="text-danger"
      >
        {{ error }}
        <a
          href="#"
          class="ml-50"
          @click.prevent="load"
        >{{ $t('involvedPanel.careLine.actions.retryLink') }}</a>
      </small>
      <small
        v-else-if="!careLines.length"
        class="text-muted"
      >
        {{ $t('involvedPanel.careLine.empty.noCareLinesAvailable') }}
      </small>
    </template>
  </div>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import axios from 'axios'
import involvedPanelMixin from './involvedPanelMixin'

export default {
  name: 'CareLinePanel',

  components: { BSpinner },

  mixins: [involvedPanelMixin],

  data() {
    return {
      careLines: [],
      selected: null,
      saved: null,
      setBy: null,
      setAt: null,
      loading: false,
      saving: false,
      error: null,
    }
  },

  computed: {
    selectedDescription() {
      const atual = this.careLines.find(c => c.id === this.selected)
      return atual && atual.description ? atual.description : null
    },
  },

  watch: {
    occurrenceInvolvedId() {
      this.load()
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    // Chamado pelo mixin ao reativar a aba (keep-alive): evita dados velhos sem F5.
    refresh() {
      return this.load()
    },

    async load() {
      if (!this.occurrenceInvolvedId) return
      this.loading = true
      this.error = null
      try {
        const [involved, lines] = await Promise.all([
          axios.get(`${this.$store.state.api}occurrence_involveds/${this.occurrenceInvolvedId}/linha-cuidado`),
          this.fetchCareLines(),
        ])
        const careLineId = involved.data.data.care_line_id
        this.selected = careLineId ? Number(careLineId) : null
        this.saved = this.selected
        this.setBy = involved.data.data.set_by || null
        this.setAt = involved.data.data.set_at || null
        this.careLines = lines.filter(c => Number(c.status) === 1 || c.id === this.selected)
      } catch (e) {
        this.careLines = []
        this.error = e.response && e.response.status
          ? this.$t('involvedPanel.careLine.errors.loadingWithStatus', { status: e.response.status })
          : this.$t('involvedPanel.careLine.errors.loadingFailed')
      } finally {
        this.loading = false
      }
    },

    async fetchCareLines() {
      const node = this.$store.state.selected_node
      if (!this.disciplineId || !node) return []
      const res = await axios.get(`${this.$store.state.api}care_lines/for-node`, {
        params: {
          node_id: node.id,
          discipline_id: Number(this.disciplineId),
          with_descendants: 1,
        },
      })
      return res.data.data || []
    },

    choose(id) {
      if (this.saving || this.selected === id) return
      this.selected = id
      this.change()
    },

    async change() {
      this.saving = true
      this.error = null
      try {
        const res = await axios.put(
          `${this.$store.state.api}occurrence_involveds/${this.occurrenceInvolvedId}/linha-cuidado`,
          { care_line_id: this.selected },
        )
        this.saved = this.selected
        this.setBy = res.data.data.set_by || null
        this.setAt = res.data.data.set_at || null
        this.$emit('changed', this.selected)
      } catch (e) {
        this.error = e.response?.data?.message || this.$t('involvedPanel.careLine.errors.savingFailed')
        this.selected = this.saved
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.cl-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.cl-chip {
  padding: 6px 14px;
  border: 1px solid #ebe9f1;
  border-radius: 14px;
  background: #fff;
  font-size: 12.5px;
  color: #6e6b7b;
  cursor: pointer;
  transition: border-color .15s, color .15s, background .15s;
}

.cl-chip:hover:not(:disabled):not(.cl-chip--on) {
  border-color: #7367f0;
  color: #7367f0;
}

.cl-chip:disabled { cursor: default; opacity: .65; }

.cl-chip--on {
  border-color: #7367f0;
  background: #7367f0;
  color: #fff;
  font-weight: 600;
}

.cl-desc {
  font-size: 12px;
  color: #6e6b7b;
  margin: 0 0 4px;
}

.dark-layout .cl-chip { background: #283046; border-color: #3b4253; color: #b4b7bd; }
.dark-layout .cl-chip--on { background: #7367f0; border-color: #7367f0; color: #fff; }
</style>
